/**
 * Ticket Form Data Types
 *
 * Types for ticket creation forms (IT and User self-service)
 */

import type {
  TicketType,
  IncidentCategory,
  IncidentSubcategory,
  RequestCategory,
  RequestSubcategory,
  ProblemCategory,
  ProblemSubcategory,
  Priority,
  Location,
  RequesterInfo,
  TicketChannel,
  AssignmentGroup,
  ClientLOB,
  LOBProgram,
  LOBSite,
  Urgency,
  ApprovalStatus,
  ChangeType,
  ChangeCategory,
  RiskLevel,
} from './ticket';

// ============================================
// IT TICKET CREATION FORM (New Two-Column Layout)
// ============================================

export interface ITTicketFormData {
  // === Read-only fields (auto-generated) ===
  /** Auto-generated ticket number */
  ticketNumber: string;
  /** Created timestamp */
  createdAt: Date;
  /** Opened by (logged-in user) */
  openedById: string;
  openedByName: string;

  // === Left Column Fields ===
  /** Selected requester from user search */
  caller: RequesterInfo | null;
  /** How the ticket was received */
  channel: TicketChannel | '';
  /** Client Line of Business (auto-filled from caller) */
  clientLOB: ClientLOB | '';
  /** Program within the LOB (auto-filled from caller) */
  lobProgram: LOBProgram | '';
  /** Physical site location (auto-filled from caller) */
  lobSite: LOBSite | '';
  /** Category based on ticket type */
  category: IncidentCategory | RequestCategory | ProblemCategory | '';
  /** Subcategory within selected category */
  subcategory: IncidentSubcategory | RequestSubcategory | ProblemSubcategory | '';
  /** Urgency level */
  urgency: Urgency;

  // === Right Column Fields ===
  /** Assignment group for the ticket */
  assignmentGroup: AssignmentGroup | '';
  /** Brief description (auto-generated from caller info) */
  shortDescription: string;
  /** Detailed description (filled by tech) */
  description: string;

  // === Hidden/Derived Fields ===
  /** Ticket type: Incident, Request, Problem, or Change */
  ticketType: TicketType;
  /** Priority (derived from urgency + impact) */
  priority: Priority;

  // === Request-specific Fields ===
  /** Approval status for Request tickets */
  requestApprovalStatus: ApprovalStatus;
  /** Date the request is needed by */
  requestNeededByDate: string;
  /** Name of the item being requested */
  requestedItemName: string;

  // === Problem-specific Fields ===
  /** Detailed problem statement (min 50 chars) */
  problemStatement: string;
  /** Root cause analysis */
  rootCause: string;
  /** Temporary workaround */
  workaround: string;
  /** IDs of linked incident tickets */
  linkedIncidentIds: string[];

  // === Change-specific Fields ===
  /** Type of change: Standard, Normal, Emergency */
  changeType: ChangeType;
  /** Category of change */
  changeCategory: ChangeCategory;
  /** Risk level assessment */
  riskLevel: RiskLevel;
  /** Change approval status */
  changeApprovalStatus: ApprovalStatus;
  /** Plan to revert the change if needed (min 20 chars) */
  rollbackPlan: string;
  /** Planned implementation start */
  implementationStart: string;
  /** Planned implementation end */
  implementationEnd: string;
}

export interface ITTicketFormErrors {
  caller?: string;
  channel?: string;
  clientLOB?: string;
  lobProgram?: string;
  lobSite?: string;
  category?: string;
  subcategory?: string;
  urgency?: string;
  assignmentGroup?: string;
  shortDescription?: string;
  description?: string;
  general?: string;
  // Request-specific errors
  requestApprovalStatus?: string;
  requestNeededByDate?: string;
  requestedItemName?: string;
  // Problem-specific errors
  problemStatement?: string;
  rootCause?: string;
  workaround?: string;
  linkedIncidentIds?: string;
  // Change-specific errors
  changeType?: string;
  changeCategory?: string;
  riskLevel?: string;
  changeApprovalStatus?: string;
  rollbackPlan?: string;
  implementationStart?: string;
  implementationEnd?: string;
}

/** Generate a ticket number with TKT prefix */
export function generateTicketNumber(): string {
  const timestamp = Date.now().toString(36).toUpperCase();
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `TKT${timestamp}${random}`;
}

/** Generate short description from caller info and category */
export function generateShortDescription(
  caller: RequesterInfo | null,
  clientLOB: string,
  lobProgram: string,
  lobSite: string,
  category: string,
  subcategory: string
): string {
  if (!caller) return '';

  const parts: string[] = [];

  // Site | Name | Employee ID | Client LOB | Program | Category | Subcategory
  const site = lobSite || caller.lobSite;
  if (site) {
    parts.push(site);
  }

  // Full Name
  parts.push(caller.name);

  // Employee Number
  if (caller.employeeId) {
    parts.push(caller.employeeId);
  }

  // Client LOB
  const lob = clientLOB || caller.clientLOB;
  if (lob) {
    parts.push(lob);
  }

  // Program
  const program = lobProgram || caller.lobProgram;
  if (program) {
    parts.push(program);
  }

  // Category
  if (category) {
    parts.push(category);
  }

  // Subcategory
  if (subcategory) {
    parts.push(subcategory);
  }

  return parts.join(' | ');
}

/** Default values for IT ticket creation form */
export function createDefaultITTicketForm(
  openedById: string,
  openedByName: string
): ITTicketFormData {
  return {
    ticketNumber: generateTicketNumber(),
    createdAt: new Date(),
    openedById,
    openedByName,
    caller: null,
    channel: '',
    clientLOB: '',
    lobProgram: '',
    lobSite: '',
    category: '',
    subcategory: '',
    urgency: 'Medium',
    assignmentGroup: '',
    shortDescription: '',
    description: '',
    ticketType: 'Incident',
    priority: 'P3',
    // Request-specific defaults
    requestApprovalStatus: 'Not Required',
    requestNeededByDate: '',
    requestedItemName: '',
    // Problem-specific defaults
    problemStatement: '',
    rootCause: '',
    workaround: '',
    linkedIncidentIds: [],
    // Change-specific defaults
    changeType: 'Normal',
    changeCategory: 'Application',
    riskLevel: 'Low',
    changeApprovalStatus: 'Pending',
    rollbackPlan: '',
    implementationStart: '',
    implementationEnd: '',
  };
}

// ============================================
// LEGACY IT TICKET FORM (for backward compatibility)
// ============================================

export interface LegacyITTicketFormData {
  /** ServiceNow ticket number (e.g., INC0001234) */
  ticketNumber: string;
  /** Brief description of the issue or request */
  shortDescription: string;
  /** Incident or Request */
  ticketType: TicketType;
  /** Category based on ticket type */
  category: IncidentCategory | RequestCategory | '';
  /** Subcategory within selected category */
  subcategory: IncidentSubcategory | RequestSubcategory | '';
  /** Priority level P1-P4 */
  priority: Priority;
  /** Location/site for the ticket */
  location: Location | '';
  /** Selected requester from user search */
  requester: RequesterInfo | null;
  /** Additional notes or details */
  notes: string;
}

/** @deprecated Use ITTicketFormData instead */
export type ITTicketFormDataLegacy = LegacyITTicketFormData;

export const DEFAULT_IT_TICKET_FORM: LegacyITTicketFormData = {
  ticketNumber: '',
  shortDescription: '',
  ticketType: 'Incident',
  category: '',
  subcategory: '',
  priority: 'P3',
  location: '',
  requester: null,
  notes: '',
};

// ============================================
// USER SELF-SERVICE FORM
// Simplified form for end users
// ============================================

export interface UserSubmitFormData {
  /** Brief description of the issue or request */
  shortDescription: string;
  /** Incident or Request */
  ticketType: TicketType;
  /** Category based on ticket type */
  category: IncidentCategory | RequestCategory | '';
  /** Subcategory within selected category */
  subcategory: IncidentSubcategory | RequestSubcategory | '';
  /** Priority level P1-P4 */
  priority: Priority;
  /** Additional notes or details */
  notes: string;
}

export interface UserSubmitFormErrors {
  shortDescription?: string;
  ticketType?: string;
  category?: string;
  subcategory?: string;
  priority?: string;
  notes?: string;
  general?: string;
}

/** Default values for user self-service form */
export const DEFAULT_USER_SUBMIT_FORM: UserSubmitFormData = {
  shortDescription: '',
  ticketType: 'Incident',
  category: '',
  subcategory: '',
  priority: 'P3',
  notes: '',
};

// ============================================
// VALIDATION CONSTANTS
// ============================================

export const TICKET_VALIDATION = {
  /** Regex pattern for ServiceNow ticket numbers (3 letters + 7 digits) */
  TICKET_NUMBER_PATTERN: /^[A-Za-z]{3}\d{7}$/,
  /** Minimum length for short description */
  DESCRIPTION_MIN_LENGTH: 5,
  /** Maximum length for short description */
  DESCRIPTION_MAX_LENGTH: 200,
  /** Maximum length for detailed description */
  DETAIL_DESCRIPTION_MAX_LENGTH: 2000,
  /** Maximum length for notes in IT form */
  IT_NOTES_MAX_LENGTH: 500,
  /** Maximum length for notes in user form */
  USER_NOTES_MAX_LENGTH: 300,
} as const;

// ============================================
// PRIORITY DESCRIPTIONS (for user guidance)
// ============================================

export const PRIORITY_DESCRIPTIONS: Record<Priority, string> = {
  P1: 'Critical - Business stopped, immediate attention required',
  P2: 'High - Major impact, work significantly affected',
  P3: 'Medium - Work affected but can continue with workaround',
  P4: 'Low - Minor issue, no immediate impact',
};

// ============================================
// URGENCY DESCRIPTIONS
// ============================================

export const URGENCY_DESCRIPTIONS: Record<Urgency, string> = {
  High: 'Work cannot continue, immediate resolution needed',
  Medium: 'Work affected but can continue with limitations',
  Low: 'Minor inconvenience, can wait for resolution',
};

// ============================================
// VALIDATION HELPERS
// ============================================

/**
 * Validate ServiceNow ticket number format
 */
export function isValidTicketNumber(ticketNumber: string): boolean {
  return TICKET_VALIDATION.TICKET_NUMBER_PATTERN.test(ticketNumber);
}

// Type-specific validation constants
export const PROBLEM_VALIDATION = {
  MIN_STATEMENT_LENGTH: 50,
  MAX_STATEMENT_LENGTH: 1000,
  MAX_ROOT_CAUSE_LENGTH: 2000,
  MAX_WORKAROUND_LENGTH: 1000,
} as const;

export const CHANGE_VALIDATION = {
  MIN_ROLLBACK_LENGTH: 20,
  MAX_ROLLBACK_LENGTH: 2000,
} as const;

/**
 * Validate IT ticket creation form (new format)
 *
 * Validation rules vary by ticket type:
 * - Incident/Request: User-centric (caller, channel, LOB, program, site required)
 * - Problem/Change: IT-centric (no caller required, site optional)
 */
export function validateITTicketForm(data: ITTicketFormData): ITTicketFormErrors {
  const errors: ITTicketFormErrors = {};

  // Determine if this is a user-centric ticket type
  const isUserCentricType = data.ticketType === 'Incident' || data.ticketType === 'Request';

  // === User-centric fields (Incident/Request only) ===
  if (isUserCentricType) {
    // Caller is required for user-centric tickets
    if (!data.caller) {
      errors.caller = 'Please select a user';
    }

    // Channel is required for user-centric tickets
    if (!data.channel) {
      errors.channel = 'Please select a channel';
    }

    // Client LOB is required for user-centric tickets
    if (!data.clientLOB) {
      errors.clientLOB = 'Client LOB is required';
    }

    // LOB Program is required for user-centric tickets
    if (!data.lobProgram) {
      errors.lobProgram = 'Program is required';
    }

    // LOB Site is required for user-centric tickets
    if (!data.lobSite) {
      errors.lobSite = 'Site is required';
    }
  }
  // For Problem/Change: Site is optional, no caller/channel/LOB/program required

  // Category/Subcategory for Incident, Request, and Problem types
  // Change tickets use changeCategory instead
  if (data.ticketType === 'Incident' || data.ticketType === 'Request') {
    if (!data.category) {
      errors.category = 'Please select a category';
    }
    if (!data.subcategory) {
      errors.subcategory = 'Please select a subcategory';
    }
  }

  // Problem: Category is optional but subcategory requires category
  if (data.ticketType === 'Problem') {
    if (data.subcategory && !data.category) {
      errors.category = 'Please select a category first';
    }
  }

  // Assignment Group is required for all types
  if (!data.assignmentGroup) {
    errors.assignmentGroup = 'Please select an assignment group';
  }

  // Description is required and has min/max length
  if (!data.description.trim()) {
    errors.description = 'Description is required';
  } else if (data.description.length > TICKET_VALIDATION.DETAIL_DESCRIPTION_MAX_LENGTH) {
    errors.description = `Description cannot exceed ${TICKET_VALIDATION.DETAIL_DESCRIPTION_MAX_LENGTH} characters`;
  }

  // === Type-specific validation ===

  if (data.ticketType === 'Request') {
    // Request: Needed by date must be future if provided
    if (data.requestNeededByDate) {
      const neededBy = new Date(data.requestNeededByDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (neededBy < today) {
        errors.requestNeededByDate = 'Needed by date must be in the future';
      }
    }
  }

  if (data.ticketType === 'Problem') {
    // Problem: Problem statement is required with minimum length
    if (!data.problemStatement.trim()) {
      errors.problemStatement = 'Problem statement is required';
    } else if (data.problemStatement.length < PROBLEM_VALIDATION.MIN_STATEMENT_LENGTH) {
      errors.problemStatement = `Problem statement must be at least ${PROBLEM_VALIDATION.MIN_STATEMENT_LENGTH} characters`;
    } else if (data.problemStatement.length > PROBLEM_VALIDATION.MAX_STATEMENT_LENGTH) {
      errors.problemStatement = `Problem statement cannot exceed ${PROBLEM_VALIDATION.MAX_STATEMENT_LENGTH} characters`;
    }
  }

  if (data.ticketType === 'Change') {
    // Change: Rollback plan is required with minimum length
    if (!data.rollbackPlan.trim()) {
      errors.rollbackPlan = 'Rollback plan is required';
    } else if (data.rollbackPlan.length < CHANGE_VALIDATION.MIN_ROLLBACK_LENGTH) {
      errors.rollbackPlan = `Rollback plan must be at least ${CHANGE_VALIDATION.MIN_ROLLBACK_LENGTH} characters`;
    } else if (data.rollbackPlan.length > CHANGE_VALIDATION.MAX_ROLLBACK_LENGTH) {
      errors.rollbackPlan = `Rollback plan cannot exceed ${CHANGE_VALIDATION.MAX_ROLLBACK_LENGTH} characters`;
    }

    // Implementation window validation
    if (data.implementationStart && data.implementationEnd) {
      const start = new Date(data.implementationStart);
      const end = new Date(data.implementationEnd);
      if (end <= start) {
        errors.implementationEnd = 'Implementation end must be after start';
      }
    }
  }

  return errors;
}

/**
 * Validate user self-service form
 */
export function validateUserSubmitForm(data: UserSubmitFormData): UserSubmitFormErrors {
  const errors: UserSubmitFormErrors = {};

  // Short description
  if (!data.shortDescription.trim()) {
    errors.shortDescription = 'Description is required';
  } else if (data.shortDescription.trim().length < TICKET_VALIDATION.DESCRIPTION_MIN_LENGTH) {
    errors.shortDescription = `Description must be at least ${TICKET_VALIDATION.DESCRIPTION_MIN_LENGTH} characters`;
  } else if (data.shortDescription.trim().length > TICKET_VALIDATION.DESCRIPTION_MAX_LENGTH) {
    errors.shortDescription = `Description cannot exceed ${TICKET_VALIDATION.DESCRIPTION_MAX_LENGTH} characters`;
  }

  // Category
  if (!data.category) {
    errors.category = 'Category is required';
  }

  // Subcategory
  if (!data.subcategory) {
    errors.subcategory = 'Subcategory is required';
  }

  // Notes (optional but has max length)
  if (data.notes.length > TICKET_VALIDATION.USER_NOTES_MAX_LENGTH) {
    errors.notes = `Notes cannot exceed ${TICKET_VALIDATION.USER_NOTES_MAX_LENGTH} characters`;
  }

  return errors;
}

/**
 * Check if form has any errors
 */
export function hasFormErrors(
  errors: ITTicketFormErrors | UserSubmitFormErrors
): boolean {
  return Object.keys(errors).length > 0;
}
